import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Can } from "@/lib/authorization";
import { BulkActionBar } from "@/lib/components/bulk-action-bar";
import { CenteredSpinner } from "@/lib/components/CenteredSpinner";
import { EmptyStateWithError } from "@/lib/components/errors/EmptyStateWithError";
import { QueryErrorDisplay } from "@/lib/components/errors/QueryErrorDisplay";
import { useHolidays } from "../hooks";
import { useHolidayUIStore } from "../store/use-holiday-ui-store";
import type { HolidayFilters } from "../types";
import { HolidayCard } from "./holiday-card";

interface HolidayListProps {
  timeFilter?: "future" | "past" | "current";
  filters?: HolidayFilters;
}

export function HolidayList({ timeFilter, filters }: HolidayListProps) {
  const { t } = useTranslation("settings");
  const { futureHolidays, pastHolidays, loading, error, refetch } = useHolidays({
    ...filters,
    timeFilter,
  });

  // Select holidays based on time filter
  const holidays = timeFilter === "past" ? pastHolidays : futureHolidays;
  const { selectedHolidayIds, clearSelection, selectAll, isAllSelected, openBulkDeleteDialog } =
    useHolidayUIStore();
  const holidayIds = holidays.map((holiday) => holiday.id);

  // Loading state
  if (loading && holidays.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <CenteredSpinner size="default" />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error && holidays.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <QueryErrorDisplay
            error={{
              type: "GRAPHQL_ERROR",
              message: error.message || t("holidayList.errors.failedToLoad"),
              retryable: true,
            }}
            onRetry={refetch}
          />
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!loading && holidays.length === 0) {
    const title =
      timeFilter === "past"
        ? t("holidayList.emptyState.past.title")
        : t("holidayList.emptyState.upcoming.title");
    const description =
      timeFilter === "past"
        ? t("holidayList.emptyState.past.description")
        : t("holidayList.emptyState.upcoming.description");

    return (
      <Card>
        <CardContent className="py-12">
          <EmptyStateWithError title={title} description={description} error={null} />
        </CardContent>
      </Card>
    );
  }

  // Success state
  return (
    <div className="space-y-4">
      <Can I="delete" a="Holiday">
        <BulkActionBar
          selectAllLabel={t("holidays.bulk.selectAll")}
          allSelected={isAllSelected(holidayIds)}
          onSelectAllChange={(checked) => (checked ? selectAll(holidayIds) : clearSelection())}
          selectionContent={
            selectedHolidayIds.length > 0 ? (
              <>
                <span className="text-sm font-medium">
                  {t("holidays.bulk.selected", { count: selectedHolidayIds.length })}
                </span>
                <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>
                  {t("holidays.bulk.clear")}
                </Button>
              </>
            ) : undefined
          }
          actions={
            selectedHolidayIds.length > 0 ? (
              <Button variant="destructive" size="sm" onClick={openBulkDeleteDialog}>
                <Trash2 className="mr-2 h-4 w-4" />
                {t("holidays.bulk.deleteSelected")}
              </Button>
            ) : undefined
          }
        />
      </Can>
      {holidays.map((holiday) => (
        <HolidayCard key={holiday.id} holiday={holiday} />
      ))}
    </div>
  );
}
